import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { getData } from '../../../api/indexedDBHandler'
import PairWeightTournamentProtocolPDF from './PairWeightTournamentProtocolPDF'
import { getLogoUrl } from '../../../api/Logo'
import { dateNowProtocol } from '../../../api/formatDate'

const calculateWeightRankings = (pairs, minWeight, countFish) => {
  // 1. Фильтруем только зачётную рыбу (weight >= minWeight) и недисквалифицированные команды
  const validPairs = pairs
    .map((pair) => {
      // Фильтруем рыбу по минимальному весу
      const validFishes = pair.fishList
        .filter((fish) => fish.weight >= minWeight)
        .sort((a, b) => b.weight - a.weight) // Сортируем по убыванию веса

      // Берем топ N рыб или все, если меньше N
      const topFishes = validFishes.slice(0, countFish)
      const topWeight = topFishes.reduce((sum, fish) => sum + fish.weight, 0)

      // Формируем строку с рыбами (вид: вес)
      const fishesString = topFishes.map((fish) => `${fish.weight}`).join(', ')

      return {
        ...pair,
        validFishCount: validFishes.length,
        topFishCount: topFishes.length,
        topFishWeight: topWeight,
        hasEnoughFish: validFishes.length >= countFish,
        fishesForProtocol: topFishes,
        fishesString,
      }
    })
    .filter((pair) => !pair.disqualified)

  // 2. Сортируем сначала по наличию достаточного количества рыб, затем по весу топ N
  const sortedPairs = [...validPairs].sort((a, b) => {
    // Команды с достаточным количеством рыб идут выше
    if (a.hasEnoughFish !== b.hasEnoughFish) {
      return b.hasEnoughFish - a.hasEnoughFish
    }

    // Если у обеих команд достаточно рыб - сортируем по весу топ N
    if (a.hasEnoughFish && b.hasEnoughFish) {
      return b.topFishWeight - a.topFishWeight
    }

    // Если у обеих недостаточно рыб - сортируем по количеству рыб, затем по весу
    if (b.validFishCount !== a.validFishCount) {
      return b.validFishCount - a.validFishCount
    }
    return b.topFishWeight - a.topFishWeight
  })

  // 3. Присваиваем места с учетом ничьих
  let rankedPairs = []
  let currentRank = 1

  while (currentRank <= sortedPairs.length) {
    const currentPair = sortedPairs[currentRank - 1]
    const sameRankPairs = sortedPairs.filter(
      (p) =>
        p.hasEnoughFish === currentPair.hasEnoughFish &&
        p.topFishWeight === currentPair.topFishWeight &&
        (currentPair.hasEnoughFish ||
          p.validFishCount === currentPair.validFishCount)
    )

    if (sameRankPairs.length > 1) {
      // Вычисляем средний ранг для ничьих
      const averageRank = currentRank + (sameRankPairs.length - 1) / 2

      rankedPairs.push(
        ...sameRankPairs.map((pair) => ({
          ...pair,
          overallRank: averageRank,
          isTie: sameRankPairs.length > 1,
        }))
      )
      currentRank += sameRankPairs.length
    } else {
      rankedPairs.push({
        ...currentPair,
        overallRank: currentRank,
        isTie: false,
      })
      currentRank++
    }
  }

  // 4. Добавляем дисквалифицированные команды в конец
  const disqualifiedPairs = pairs
    .filter((pair) => pair.disqualified)
    .map((pair) => ({
      ...pair,
      overallRank: pairs.length + 3,
      validFishCount: 0,
      topFishCount: 0,
      topFishWeight: 0,
      hasEnoughFish: false,
      fishesForProtocol: [],
      fishesString: '',
      isTie: false,
    }))

  return [...rankedPairs, ...disqualifiedPairs]
}

const generateProtocolBData = async (id, minWeight = 1500, countFish = 5) => {
  const resultsU = await getData('teams', 'id', id)
  if (resultsU.length === 0) throw new Error('No team data found')

  const { value: teamValue } = resultsU[0]
  const filteredTeams = Object.values(teamValue).filter(
    (teamEntry) =>
      teamEntry?.team?.status === 'Заявка одобрена' &&
      teamEntry?.team?.status_location === 1
  )

  const resultsS = await getData('stage', 'id', id)
  if (resultsS.length === 0) throw new Error('No stage data found')

  const { value: stageValue } = resultsS[0]
  // const stageArray = Object.values(stageValue).filter(Boolean)
  const stageArray = Object.values(stageValue).filter(
    (teamEntry) => teamEntry !== null && typeof teamEntry === 'object'
  )

  const resultsT = await getData('tournaments', 'id', id)
  if (resultsT.length === 0) throw new Error('No tournament data found')

  const { value: tournamentValue } = resultsT[0]
  const isFinish = tournamentValue.status_cup === 'Турнир завершен'
  const allLogo = Object.values(tournamentValue.sponsor || {})
  const administrator = Object.values(tournamentValue.administrator || [])

  const getTeamName = (teamData) => {
    return resultsT[0].competition_type === 'Рейтинговое соревнование'
      ? teamData?.team_name_ru
      : teamData?.preferred_name === 'en'
        ? teamData?.team_name_en || teamData?.team_name_ru
        : teamData?.team_name_ru
  }

  // Формируем пары
  const pairs = filteredTeams.map((team) => {
    const athletes = team.athlete || []
    // Собираем все записи fishList для данной команды
    const allFishLists = stageArray[0]
      .filter((e) => e.teamId === team.team.id)
      .flatMap((e) => e.fishList || [])

    return {
      id: team.team.id,
      teamName: getTeamName(team.team),
      sp: athletes
        .map(
          (a) =>
            `${a.fio || ''}${a.sports_category ? ` (${a.sports_category})` : ''}`
        )
        .join(', '),
      sector: team.team.sector,
      fishList: allFishLists, // Теперь содержит все рыбы из всех взвешиваний
      disqualified: team.team.status === 'Снята',
    }
  })
  console.log('pairs', pairs)
  // Рассчитываем ранги по весу топ N рыб
  const rankedPairs = calculateWeightRankings(pairs, minWeight, countFish)

  // Находим самую крупную рыбу
  const biggestFish = rankedPairs.reduce(
    (max, pair) => {
      const pairBiggest = Math.max(
        ...pair.fishesForProtocol.map((f) => f.weight || 0),
        0
      )
      return pairBiggest > max.weight
        ? { weight: pairBiggest, pair: pair.teamName }
        : max
    },
    { weight: 0, pair: '' }
  )

  // Общие показатели
  const totalFishCount = rankedPairs.reduce(
    (sum, p) => sum + p.fishList.filter((f) => f.weight >= minWeight).length,
    0
  )
  const totalWeight = rankedPairs.reduce((sum, p) => sum + p.topFishWeight, 0)

  return {
    allLogo,
    mainLogo: getLogoUrl(tournamentValue?.logocup),
    data: rankedPairs,
    administrator,
    tournament: tournamentValue,
    date: dateNowProtocol(new Date()),
    totalFishCount,
    totalWeight,
    biggestFish,
    isFinish,
    countFish,
    minWeight,
  }
}

const uploadPdfToServer = async (blob, id, countFish) => {
  const formData = new FormData()
  formData.append(
    'pdf',
    blob,
    `вес_${countFish}_рыб_протокол_${dateNowProtocol(new Date())}.pdf`
  )
  formData.append('id', id)
  formData.append('isPost', 3)
  formData.append('type', 'ProtocolWeight')

  try {
    const response = await fetch(
      'https://www.admin-protocarp.ru/php/uploads/upload.php',
      {
        method: 'POST',
        body: formData,
      }
    )
    return response.ok ? await response.json() : null
  } catch (error) {
    console.error('Upload error:', error)
    return null
  }
}

export default {
  generate: async function (id, minWeight = 1500, countFish = 5) {
    try {
      const protocolData = await generateProtocolBData(id, minWeight, countFish)
      console.log(protocolData)
      const pdfDoc = <PairWeightTournamentProtocolPDF {...protocolData} />

      const blob = await pdf(pdfDoc).toBlob()
      const uploadResult = await uploadPdfToServer(blob, id, countFish)

      return {
        blob,
        previewUrl: uploadResult || URL.createObjectURL(blob),
        data: protocolData,
      }
    } catch (error) {
      console.error('Generation error:', error)
      throw error
    }
  },
}
